import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Rocket, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";

const HeroSection: React.FC = () => {
  const { user } = useAuth();
  const [currentEmoji, setCurrentEmoji] = useState(0);
  
  // Array of tech-themed emojis with their labels
  const techEmojis = [
    { icon: <Sparkles className="h-6 w-6 text-brand-orange" />, label: "Explore" },
    { icon: <Rocket className="h-6 w-6 text-brand-orange" />, label: "Launch" },
    { icon: <Zap className="h-6 w-6 text-brand-orange" />, label: "Power Up" },
  ];

  // Animate through emojis every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentEmoji((prev) => (prev + 1) % techEmojis.length);
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="relative px-4 sm:px-6 lg:px-8 py-24 md:py-32 overflow-hidden">
      {/* Animated Background Elements - More vibrant and dynamic */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient blobs */}
        <div aria-hidden="true" className="absolute -top-40 -right-40 md:right-0 w-[500px] h-[500px] bg-brand-orange opacity-20 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-gentle"></div>
        <div aria-hidden="true" className="absolute -bottom-32 -left-40 md:left-0 w-[500px] h-[500px] bg-brand-teal opacity-20 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        
        {/* Animated background patterns */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBzdHJva2U9IiNmOTczMTYiIHN0cm9rZS13aWR0aD0iMC41IiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiIG9wYWNpdHk9IjAuMSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMTUiLz48L2c+PC9zdmc+')] opacity-10 dark:opacity-5"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-20">
        <div className="text-center">
          <motion.h1 
            className="text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl md:text-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange via-brand-teal to-brand-orange bg-size-200 animate-gradient font-poppins drop-shadow-sm">
              Discover Your Path.
            </span>
            <br />
            <motion.span 
              className="block mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
            >
              Navigate Your Future.
            </motion.span>
          </motion.h1>
          
          <motion.div 
            className="flex items-center justify-center mt-4 space-x-2"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <div className="animate-float flex items-center justify-center bg-slate-100/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-full w-12 h-12 transition-all duration-300 shadow-md">
              {techEmojis[currentEmoji].icon}
            </div>
            <p className="text-lg font-medium text-brand-orange animate-fade-in">
              {techEmojis[currentEmoji].label}
            </p>
          </motion.div>
          
          <motion.p 
            className="mt-6 max-w-2xl mx-auto text-xl text-muted-foreground"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
          >
            Connect with mentors, join clubs, and explore opportunities 
            tailored to your interests and goals.
          </motion.p>
          <motion.div 
            className="mt-10 flex justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
          >
            {user ? (
              <Button asChild size="lg" className="font-medium bg-gradient-to-r from-brand-orange to-brand-teal hover:shadow-lg hover:shadow-brand-orange/20 transition-all duration-500 animate-fade-in">
                <Link to="/startups" className="flex items-center">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4 animate-bounce-slow" />
                </Link>
              </Button>
            ) : (
              <Button asChild size="lg" className="font-medium bg-gradient-to-r from-brand-orange to-brand-teal hover:shadow-lg hover:shadow-brand-orange/20 transition-all duration-500 animate-fade-in">
                <Link to="/auth" className="flex items-center">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4 animate-bounce-slow" />
                </Link>
              </Button>
            )}
          </motion.div>

          {/* BetaList Badge */}
          <motion.div 
            className="mt-12 flex flex-col items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.7 }}
          >
            <p className="text-sm text-muted-foreground mb-2">Featured on</p>
            <a 
              target="_blank" 
              href="https://betalist.com/startups/teenvisor?utm_campaign=badge-teenvisor&amp;utm_medium=badge&amp;utm_source=badge-featured"
              rel="noopener noreferrer"
            >
              <img 
                alt="Teenvisor - The world's platform for teen futures | BetaList" 
                width="156" 
                height="54" 
                style={{ width: '156px', height: '54px' }} 
                src="https://betalist.com/badges/featured?id=123651&amp;theme=gray" 
              />
            </a>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default HeroSection;
